import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  listAllModelPortfolios,
  listModelPortfolios,
  getModelPortfolio,
  createModelPortfolio,
  updateModelPortfolio,
  deleteModelPortfolio,
  getSymbolIep,
} from '../../api/modelPortfolioService';
import './ModelPortfolioAdmin.css';

const EMPTY_ROW = { symbol: '', status: 'HOLD', recordPrice: '', maxBuyPrice: '', iep: '', weightPercent: '' };

const INITIAL_FORM = {
  name: '',
  description: '',
  riskLevel: 'MODERATE',
  themeCategory: '',
  strategyType: '',
  holdingPeriodMonths: '',
  isActive: 'true',
  rebalanceNote: '',
};

const numOrEmpty = (v) => {
  if (v === null || v === undefined) return '';
  const n = parseFloat(v.toString());
  return Number.isNaN(n) ? '' : n;
};

const riskClass = (level) => {
  const l = (level || '').toLowerCase();
  if (l === 'low') return 'mpa-badge-low';
  if (l === 'high') return 'mpa-badge-high';
  return 'mpa-badge-moderate';
};

const ModelPortfolioAdmin = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [editingId, setEditingId] = useState('');
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type, text }

  // ── Toast auto-dismiss ───────────────────────────────────────
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (type, text) => setToast({ type, text });

  // ── Load list (admin endpoint, fallback to public) ───────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      let res;
      try {
        res = await listAllModelPortfolios();
      } catch (e) {
        res = await listModelPortfolios();
      }
      setList(res.data?.data || []);
    } catch (e) {
      showToast('err', 'Failed to load portfolios.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ── Form helpers ─────────────────────────────────────────────
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setRow = (i, key, value) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const addRow = () => setRows((rs) => [...rs, { ...EMPTY_ROW }]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const totalWeight = rows.reduce((sum, r) => sum + (parseFloat(r.weightPercent) || 0), 0);
  const weightOk = Math.abs(totalWeight - 100) < 0.01;

  const fetchIep = async (i) => {
    const symbol = (rows[i].symbol || '').trim().toUpperCase();
    if (!symbol) {
      showToast('err', 'Enter a symbol first');
      return;
    }
    try {
      const res = await getSymbolIep(symbol);
      const iep = res.data?.data?.iep;
      setRow(i, 'iep', iep);
      showToast('ok', `${symbol}: IEP ₹${iep}`);
    } catch (e) {
      showToast('err', `No IEP for ${symbol}`);
    }
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setRows([{ ...EMPTY_ROW }]);
    setEditingId('');
  };

  const collectStocks = () =>
    rows
      .map((r) => ({
        symbol: (r.symbol || '').trim().toUpperCase(),
        status: r.status || 'HOLD',
        recordPrice: r.recordPrice === '' ? null : parseFloat(r.recordPrice),
        maxBuyPrice: r.maxBuyPrice === '' ? null : parseFloat(r.maxBuyPrice),
        iep: r.iep === '' ? null : parseFloat(r.iep),
        weightPercent: parseFloat(r.weightPercent) || 0,
      }))
      .filter((s) => s.symbol);

  const save = async () => {
    if (!form.name.trim()) {
      showToast('err', 'Name is required');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      riskLevel: form.riskLevel,
      themeCategory: form.themeCategory.trim(),
      strategyType: form.strategyType.trim(),
      holdingPeriodMonths: parseInt(form.holdingPeriodMonths, 10) || 0,
      isActive: form.isActive === 'true',
      stocks: collectStocks(),
      rebalanceNote: form.rebalanceNote.trim(),
    };
    try {
      if (editingId) {
        await updateModelPortfolio(editingId, payload);
        showToast('ok', 'Portfolio updated');
      } else {
        await createModelPortfolio(payload);
        showToast('ok', 'Portfolio created');
      }
      resetForm();
      loadList();
    } catch (e) {
      showToast('err', 'Save failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const edit = async (id) => {
    try {
      const res = await getModelPortfolio(id);
      const p = res.data?.data;
      if (!p) return;
      setEditingId(p._id);
      setForm({
        name: p.name || '',
        description: p.description || '',
        riskLevel: p.riskLevel || 'MODERATE',
        themeCategory: p.themeCategory || '',
        strategyType: p.strategyType || '',
        holdingPeriodMonths: p.holdingPeriodMonths || '',
        isActive: String(p.isActive),
        rebalanceNote: '',
      });
      const stockRows = (p.stocks || []).map((s) => ({
        symbol: s.symbol || '',
        status: s.status || 'HOLD',
        recordPrice: numOrEmpty(s.recordPrice),
        maxBuyPrice: numOrEmpty(s.maxBuyPrice),
        iep: numOrEmpty(s.iep),
        weightPercent: numOrEmpty(s.weightPercent),
      }));
      setRows(stockRows.length ? stockRows : [{ ...EMPTY_ROW }]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      showToast('err', 'Failed to load portfolio');
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"? It will be hidden from users.`)) return;
    try {
      await deleteModelPortfolio(id);
      showToast('ok', 'Portfolio deactivated');
      loadList();
    } catch (e) {
      showToast('err', 'Delete failed: ' + (e.response?.data?.message || e.message));
    }
  };

  return (
    <AppLayout pageBadge="Model Portfolio Admin">
      <div className="mpa-root">
        <div className="mpa-top">
          <h1>⚙️ Model Portfolio Admin</h1>
          <div className="sub">Create &amp; manage smallcase-style stock baskets</div>
        </div>

        <div className="mpa-layout">
          {/* ── Form ─────────────────────────────────────── */}
          <div className="mpa-card">
            <h2>{editingId ? '✏️ Edit Portfolio' : '➕ New Portfolio'}</h2>

            <div className="mpa-field">
              <label>Name *</label>
              <input
                type="text"
                placeholder="e.g. EV Momentum Basket"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
            </div>
            <div className="mpa-field">
              <label>Description</label>
              <textarea
                placeholder="Short description of the strategy / theme"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
            <div className="mpa-grid-3">
              <div>
                <label>Risk Level</label>
                <select
                  value={form.riskLevel}
                  onChange={(e) => setField('riskLevel', e.target.value)}
                >
                  <option value="LOW">LOW</option>
                  <option value="MODERATE">MODERATE</option>
                  <option value="HIGH">HIGH</option>
                </select>
              </div>
              <div>
                <label>Theme Category</label>
                <input
                  type="text"
                  placeholder="e.g. EV, Banking"
                  value={form.themeCategory}
                  onChange={(e) => setField('themeCategory', e.target.value)}
                />
              </div>
              <div>
                <label>Strategy Type</label>
                <input
                  type="text"
                  placeholder="e.g. Momentum, Value"
                  value={form.strategyType}
                  onChange={(e) => setField('strategyType', e.target.value)}
                />
              </div>
            </div>
            <div className="mpa-grid-2">
              <div>
                <label>Holding Period (months)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 12"
                  value={form.holdingPeriodMonths}
                  onChange={(e) => setField('holdingPeriodMonths', e.target.value)}
                />
              </div>
              <div>
                <label>Active</label>
                <select
                  value={form.isActive}
                  onChange={(e) => setField('isActive', e.target.value)}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <h2 style={{ marginTop: '1rem' }}>Holdings</h2>
            <div className="mpa-stock-head">
              <div>Symbol</div>
              <div>Status</div>
              <div>Record ₹</div>
              <div>Max Buy ₹</div>
              <div>IEP ₹ (auto)</div>
              <div>Weight %</div>
              <div />
            </div>
            {rows.map((r, i) => (
              <div className="mpa-stock-row" key={i}>
                <input
                  type="text"
                  placeholder="SYMBOL"
                  value={r.symbol}
                  onChange={(e) => setRow(i, 'symbol', e.target.value)}
                />
                <select
                  value={r.status}
                  onChange={(e) => setRow(i, 'status', e.target.value)}
                >
                  <option value="HOLD">HOLD</option>
                  <option value="EXIT">EXIT</option>
                  <option value="NEW">NEW</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Record"
                  value={r.recordPrice}
                  onChange={(e) => setRow(i, 'recordPrice', e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max buy"
                  value={r.maxBuyPrice}
                  onChange={(e) => setRow(i, 'maxBuyPrice', e.target.value)}
                />
                <div className="mpa-iep-cell">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="IEP"
                    value={r.iep}
                    onChange={(e) => setRow(i, 'iep', e.target.value)}
                  />
                  <button
                    type="button"
                    className="mpa-btn mpa-btn-ghost mpa-btn-sm"
                    title="Fetch latest IEP from market data"
                    onClick={() => fetchIep(i)}
                  >
                    ⟳
                  </button>
                </div>
                <input
                  type="number"
                  step="0.1"
                  placeholder="%"
                  value={r.weightPercent}
                  onChange={(e) => setRow(i, 'weightPercent', e.target.value)}
                />
                <button
                  type="button"
                  className="mpa-row-remove"
                  title="Remove"
                  onClick={() => removeRow(i)}
                >
                  ×
                </button>
              </div>
            ))}
            <div className={`mpa-weight-note ${weightOk ? 'mpa-weight-ok' : 'mpa-weight-bad'}`}>
              Total weight: {totalWeight.toFixed(1)}%
              {weightOk ? ' ✓' : ' (should sum to ~100%)'}
            </div>
            <button type="button" className="mpa-btn mpa-btn-ghost mpa-btn-sm" onClick={addRow}>
              + Add Stock
            </button>

            <div className="mpa-field" style={{ marginTop: '1rem' }}>
              <label>Rebalance Note (optional — shown to users in timeline)</label>
              <textarea
                placeholder="e.g. Booking ITC on target, adding LT on breakout"
                value={form.rebalanceNote}
                onChange={(e) => setField('rebalanceNote', e.target.value)}
                rows={2}
              />
            </div>

            <div className="mpa-actions">
              <button className="mpa-btn mpa-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Portfolio'}
              </button>
              <button className="mpa-btn mpa-btn-ghost" onClick={resetForm} disabled={saving}>
                Reset / New
              </button>
            </div>
          </div>

          {/* ── List ─────────────────────────────────────── */}
          <div className="mpa-card">
            <h2>
              📋 Existing Portfolios{' '}
              <span className="mpa-badge mpa-badge-soft">{list.length}</span>
            </h2>
            {loadingList ? (
              <div style={{ color: 'var(--mpa-text-3)', fontSize: '0.85rem' }}>Loading…</div>
            ) : list.length === 0 ? (
              <div style={{ color: 'var(--mpa-text-3)', fontSize: '0.85rem' }}>
                No portfolios yet.
              </div>
            ) : (
              list.map((p) => (
                <div key={p._id} className={`mpa-item${p.isActive ? '' : ' inactive'}`}>
                  <h3>
                    {p.name}
                    <span className={`mpa-badge ${riskClass(p.riskLevel)}`}>
                      {p.riskLevel || 'MODERATE'}
                    </span>
                    {!p.isActive && <span className="mpa-badge mpa-badge-off">Inactive</span>}
                  </h3>
                  <div className="meta">
                    {(p.stocks || []).length} stocks
                    {p.themeCategory ? ` · ${p.themeCategory}` : ''}
                    {p.strategyType ? ` · ${p.strategyType}` : ''}
                    {p.holdingPeriodMonths ? ` · ${p.holdingPeriodMonths}mo` : ''}
                  </div>
                  <div className="row-actions">
                    <button
                      className="mpa-btn mpa-btn-ghost mpa-btn-sm"
                      onClick={() => edit(p._id)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="mpa-btn mpa-btn-danger mpa-btn-sm"
                      onClick={() => remove(p._id, p.name)}
                    >
                      🗑️ Deactivate
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {toast && <div className={`mpa-toast ${toast.type}`}>{toast.text}</div>}
    </AppLayout>
  );
};

export default ModelPortfolioAdmin;
